import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useEffect } from 'react';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import type {
  ResponseActionMatrixDTO,
  ResponseActionPayload
} from './TicketResponseActions.types';

export const useFetchResponseActionMatrix = (organizationLabel?: string) => {
  const handleError = useHandleError();

  const { data, isError, error } = useQuery<
    ResponseActionMatrixDTO,
    AxiosError
  >({
    queryKey: [
      `organization_settings/${organizationLabel}/case_response_action_availability`
    ],
    enabled: !!organizationLabel && organizationLabel !== 'all',
    staleTime: 60 * 5000
  });

  useEffect(() => {
    if (isError) {
      handleError(error, 'Failure to fetch response action matrix.');
    }
  }, [isError, error]);

  return { data };
};

export const useCreateResponseAction = (
  caseId: string,
  keyToInvalidate?: string
) => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (actionInfo: ResponseActionPayload) => {
      return axiosClient.post(`cases/${caseId}/response_actions`, actionInfo);
    },
    onError: (error: AxiosError) => {
      queryClient.invalidateQueries({
        queryKey: [keyToInvalidate]
      });
      handleError(error, 'Failure to create response action.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keyToInvalidate]
      });
    }
  });
};
